export interface IOptions {
  jsonpCallback: string,
  jsonpCallbackFunction: string | null,
  timeout?: number,
  charset?: string,
  nonce?: string,
  referrerPolicy?: string,
  crossorigin?: boolean
};

const defaultOptions: IOptions = {
  timeout: 5000,
  jsonpCallback: 'callback',
  jsonpCallbackFunction: null,
};

function generateCallbackFunction() {
  return `jsonp_${Date.now()}_${Math.ceil(Math.random() * 100000)}`;
}

function clearFunction(functionName: string) {
  // IE8 throws an exception when you try to delete a property on window
  // http://stackoverflow.com/a/1824228/751089
  try {
    delete (window as unknown as { [key: string]: any })[functionName];
  } catch (e) {
    (window as unknown as { [key: string]: any })[functionName] = undefined;
  }
}

function removeScript(scriptId: string) {
  const script = document.getElementById(scriptId);
  if (script) {
    document.getElementsByTagName('head')[0].removeChild(script);
  }
}

interface ICallbackFunctionParams {
  resolve: (...args: any) => void;
  timeoutId: number;
  scriptId: string;
  callbackFunction: string;
}

function fetchJsonp(_url: string, options = defaultOptions) {
  // to avoid param reassign
  let url = _url;
  const timeout = options.timeout || defaultOptions.timeout;
  const jsonpCallback = options.jsonpCallback || defaultOptions.jsonpCallback;

  let timeoutId: number;

  return new Promise<{ ok: boolean, json: () => Promise<any> }>((resolve, reject) => {
    const callbackFunction = options.jsonpCallbackFunction || generateCallbackFunction();
    const scriptId = `${jsonpCallback}_${callbackFunction}`;

    // @ts-ignore
    window[callbackFunction] = (...args: [ICallbackFunctionParams, any]) => {
      args[0].resolve({
        ok: true,
        // keep consistent with fetch API
        json: () => Promise.resolve(args[1]),
      });

      if (args[0].timeoutId) window.clearTimeout(args[0].timeoutId);

      removeScript(args[0].scriptId);

      clearFunction(args[0].callbackFunction);
    };
    // @ts-ignore
    window[callbackFunction] = window[callbackFunction].bind(null, {
      resolve, timeoutId, scriptId, callbackFunction
    });

    // Check if the user set their own params, and if not add a ? to start a list of params
    url += (url.indexOf('?') === -1) ? '?' : '&';

    const jsonpScript = document.createElement('script');
    jsonpScript.setAttribute('src', `${url}${jsonpCallback}=${callbackFunction}`);
    if (options.charset) {
      jsonpScript.setAttribute('charset', options.charset);
    }
    if (options.nonce) {
      jsonpScript.setAttribute('nonce', options.nonce);
    }
    if (options.referrerPolicy) {
      jsonpScript.setAttribute('referrerPolicy', options.referrerPolicy);
    }
    if (options.crossorigin) {
      jsonpScript.setAttribute('crossorigin', 'true');
    }
    jsonpScript.id = scriptId;
    document.getElementsByTagName('head')[0].appendChild(jsonpScript);

    const onTimeout = (rejectPromise: (r: Error) => void) => {
      rejectPromise(new Error(`JSONP request to ${_url} timed out`));

      clearFunction(callbackFunction);
      removeScript(scriptId);
      // @ts-ignore
      window[callbackFunction] = () => {
        clearFunction(callbackFunction);
      };
    };

    timeoutId = window.setTimeout(onTimeout.bind(null, reject), timeout);

    const onError = (
      args: {
        reject: (r: Error) => void,
        callbackFunction: string,
        scriptId: string,
        timeoutId: number
      }
    ) => {
      args.reject(new Error(`JSONP request to ${_url} failed`));

      clearFunction(args.callbackFunction);
      removeScript(args.scriptId);
      if (args.timeoutId) window.clearTimeout(args.timeoutId);
    };
    // Caught if got 404/500
    jsonpScript.onerror = onError.bind(null, { reject, callbackFunction, scriptId, timeoutId });
  });
}

export default fetchJsonp;